import React from 'react';
import { Link } from 'react-router-dom';
import { Cloud, Linkedin, Mail, Phone, MapPin } from 'lucide-react';

const FooterLink = ({ to, children }) => (
  <li>
    <Link
      to={to}
      className="text-slate-300 hover:text-white transition-colors flex items-center group text-sm"
    >
      <span className="w-1.5 h-1.5 bg-c4p-blue rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      {children}
    </Link>
  </li>
);

const Footer = () => {
  return (
    <footer className="relative bg-c4p-navy text-white overflow-hidden">
      {/* Subtle background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-c4p-blue/4 rounded-full translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-c4p-green/3 rounded-full -translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="container mx-auto px-6 py-16 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">

          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-c4p-blue flex items-center justify-center shadow-lg">
                <Cloud className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold">Cloud4Partners™</div>
                <div className="text-xs text-c4p-green font-semibold">The SAP Business One Partner Platform</div>
              </div>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed mb-5 max-w-xs">
              The complete platform for delivering and scaling SAP Business One in the cloud. Built exclusively for SAP Business One partners.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.linkedin.com/company/cloud4partners/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/8 hover:bg-c4p-blue flex items-center justify-center transition-all duration-200 border border-white/10"
                aria-label="Cloud4Partners on LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-sm font-bold mb-5 text-white uppercase tracking-wider">Products</h3>
            <ul className="space-y-3">
              <FooterLink to="/c4p-standard">C4P Standard</FooterLink>
              <FooterLink to="/c4p-advanced">C4P Advanced</FooterLink>
              <FooterLink to="/mt-pro">C4P MTPro</FooterLink>
              <FooterLink to="/sap-b1-cloud-hosting">Compare plans</FooterLink>
            </ul>
          </div>

          {/* Platform */}
          <div>
            <h3 className="text-sm font-bold mb-5 text-white uppercase tracking-wider">Platform</h3>
            <ul className="space-y-3">
              <FooterLink to="/architecture-4">Architecture 4.0</FooterLink>
              <FooterLink to="/migration-factory">Migration Factory</FooterLink>
              <FooterLink to="/security-compliance">Security &amp; Compliance</FooterLink>
              <FooterLink to="/infrastructure-service">AWS Infrastructure</FooterLink>
              <FooterLink to="/disaster-recovery">Disaster Recovery</FooterLink>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-bold mb-5 text-white uppercase tracking-wider">Company</h3>
            <ul className="space-y-3">
              <FooterLink to="/about-us">About Us</FooterLink>
              <FooterLink to="/partners">Partners</FooterLink>
              <FooterLink to="/testimonials">Testimonials</FooterLink>
              <FooterLink to="/blog-news">Blog / News</FooterLink>
              <FooterLink to="/contact">Contact</FooterLink>
              <FooterLink to="/faqs">FAQs</FooterLink>
            </ul>
          </div>
        </div>

        {/* Contact strip */}
        <div className="border-t border-white/8 pt-8 mb-8">
          <div className="flex flex-wrap gap-6 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-c4p-blue flex-shrink-0" />
              <span>NETPark, Sedgefield, County Durham, TS21 3FD</span>
            </div>
            <a href="tel:+441789777466" className="flex items-center gap-2 hover:text-white transition-colors">
              <Phone className="w-4 h-4 text-c4p-blue flex-shrink-0" />
              +44 (0)1789 777 466
            </a>
            <a href="mailto:info@myc4p.com" className="flex items-center gap-2 hover:text-white transition-colors">
              <Mail className="w-4 h-4 text-c4p-blue flex-shrink-0" />
              info@myc4p.com
            </a>
          </div>
        </div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-slate-400 text-xs">
            © 2025 Cloud4Partners™. All rights reserved.
          </div>
          <div className="flex gap-6 text-xs">
            <Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-conditions" className="text-slate-400 hover:text-white transition-colors">
              Terms &amp; Conditions
            </Link>
          </div>
        </div>

        {/* Official partner logos */}
        <div className="mt-8 flex flex-wrap justify-center items-center gap-6 md:gap-10">

          {/* SAP Business One */}
          <img
            src="/logos/sap-b1-white.png"
            alt="SAP Business One Partner"
            className="h-8 w-auto object-contain"
            style={{ mixBlendMode: 'screen' }}
          />

          <div className="hidden md:block w-px h-10 bg-white/10" />

          {/* Powered by AWS */}
          <img
            src="/logos/powered-by-aws-white.png"
            alt="Powered by AWS"
            className="h-9 w-auto object-contain"
            style={{ mixBlendMode: 'screen' }}
          />

          <div className="hidden md:block w-px h-10 bg-white/10" />

          {/* AWS Advanced Partner */}
          <div className="bg-white/90 rounded-lg px-3 py-1.5">
            <img
              src="/logos/aws-advanced-partner.png"
              alt="AWS Advanced Partner"
              className="h-9 w-auto object-contain"
            />
          </div>

        </div>
      </div>
    </footer>
  );
};

export default Footer;
